import React, { useEffect, useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
// No need for Tabs components
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { usePlayerSimilarity } from '../lib/player-similarity.context';
import { cn } from '../lib/utils';
import { SimilarityCriteria } from '../types/player-similarity';

// No preset configurations needed

export const SimilarityCriteriaConfig: React.FC = () => {
  const {
    selectedPlayer,
    similarityCriteria,
    setSimilarityCriteria,
    totalWeight,
    isCriteriaValid,
    performSimilaritySearch,
    isSearchingSimilar
  } = usePlayerSimilarity();
  
  // No need for activeTab or customCriteria state
  
  // Update goals weight based on position when player is selected
  useEffect(() => {
    if (selectedPlayer?.position) {
      const isStriker = selectedPlayer.position.toLowerCase().includes('striker') || 
                        selectedPlayer.position.toLowerCase().includes('forward');
      
      if (isStriker) {
        // For strikers/forwards, keep the default 20% for goals
        setSimilarityCriteria(prev => ({
          ...prev,
          goals: 20,
          position: 20 // Ensure position is at default 20%
        }));
      } else {
        // For non-forwards, set goals to 0% and redistribute that 20% to position
        setSimilarityCriteria(prev => ({
          ...prev,
          goals: 0,
          position: 40 // Add the 20% from goals to position
        }));
      }
    }
  }, [selectedPlayer?.position, setSimilarityCriteria]);

  const handleWeightChange = (criterion: keyof typeof similarityCriteria, value: number | string) => {
    const numValue = typeof value === 'string' ? (parseInt(value, 10) || 0) : value;
    
    if (typeof similarityCriteria[criterion] === 'number') {
      setSimilarityCriteria({
        ...similarityCriteria,
        [criterion]: numValue
      });
    } else if (typeof similarityCriteria[criterion] === 'object' && 'weight' in similarityCriteria[criterion]) {
      setSimilarityCriteria({
        ...similarityCriteria,
        [criterion]: {
          ...similarityCriteria[criterion],
          weight: numValue
        }
      });
    }
  };

  const handleRangeChange = (criterion: 'age' | 'height' | 'marketValue' | 'minutesPlayed', value: number | string) => {
    const numValue = typeof value === 'string' ? (parseInt(value, 10) || 0) : value;
    
    setSimilarityCriteria({
      ...similarityCriteria,
      [criterion]: {
        ...similarityCriteria[criterion],
        range: numValue
      }
    });
  };
  
  // No need for getWeightPercentage function

  if (!selectedPlayer) return null;

  return (
    <div className="mt-6 bg-[#121212] rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-[#CECFD2]">Similarity Criteria</h2>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[#CECFD2]">Total:</span>
          <span 
            className={cn(
              "px-2 py-1 rounded-md font-medium",
              isCriteriaValid 
                ? "bg-[#26222E] text-[#CECFD2]" 
                : "bg-[#26222E] text-red-400"
            )}
          >
            {totalWeight}%
          </span>
        </div>
      </div>
      
      {!isCriteriaValid && (
        <div className="mb-4 p-3 rounded-lg bg-[#26222E] text-red-400 text-sm">
          Totals must add to 100%
        </div>
      )}

      <div className="space-y-4">
        <p className="text-sm text-[#CECFD2]">
          Adjust the criteria to find players similar to {selectedPlayer.name}.
        </p>
        
        {/* No weight distribution visualization */}

        {/* Compact Criteria Grid */}
        <div className="border border-gray-500 shadow-lg rounded-lg overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-500">
            {/* Column 1: Player Attributes */}
            <div className="p-4 bg-[#121212]">
              <h3 className="text-sm font-bold mb-3 text-[#CECFD2]">Player Attributes</h3>
              
              {/* Position */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="position-weight" className="text-xs font-medium text-[#CECFD2]">Position</label>
                  <span className="text-xs font-medium text-[#CECFD2]">{similarityCriteria.position}%</span>
                </div>
                <Slider
                  id="position-weight"
                  min={0}
                  max={100}
                  step={5}
                  value={[similarityCriteria.position]}
                  onValueChange={(value) => handleWeightChange('position', value[0])}
                  className="bg-[#26222E]"
                />
                <p className="text-xs text-[#CECFD2] opacity-75 mt-1">
                  {selectedPlayer.position?.toLowerCase().includes('striker') || selectedPlayer.position?.toLowerCase().includes('forward')
                    ? 'Default: 20% for all positions'
                    : 'Default: 40% for non-forwards (+20% from goals)'}
                </p>
              </div>
              
              {/* Age */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="age-weight" className="text-xs font-medium text-[#CECFD2]">Age</label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-[#CECFD2]">{similarityCriteria.age.weight}%</span>
                    <span className="text-xs text-[#CECFD2] opacity-75">±{similarityCriteria.age.range}y</span>
                    <input
                      type="range"
                      min={1}
                      max={10}
                      value={similarityCriteria.age.range}
                      onChange={(e) => handleRangeChange('age', e.target.value)}
                      className="w-12 h-1 bg-[#26222E]"
                    />
                  </div>
                </div>
                <Slider
                  id="age-weight"
                  min={0}
                  max={100}
                  step={5}
                  value={[similarityCriteria.age.weight]}
                  onValueChange={(value) => handleWeightChange('age', value[0])}
                  className="bg-[#26222E]"
                />
              </div>
              
              {/* Height */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="height-weight" className="text-xs font-medium text-[#CECFD2]">Height</label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-[#CECFD2]">{similarityCriteria.height.weight}%</span>
                    <span className="text-xs text-[#CECFD2] opacity-75">±{similarityCriteria.height.range}cm</span>
                    <input
                      type="range"
                      min={1}
                      max={20}
                      value={similarityCriteria.height.range}
                      onChange={(e) => handleRangeChange('height', e.target.value)}
                      className="w-12 h-1 bg-[#26222E]"
                    />
                  </div>
                </div>
                <Slider
                  id="height-weight"
                  min={0}
                  max={100}
                  step={5}
                  value={[similarityCriteria.height.weight]}
                  onValueChange={(value) => handleWeightChange('height', value[0])}
                  className="bg-[#26222E]"
                />
              </div>
              
              {/* Market Value */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="market-value-weight" className="text-xs font-medium text-[#CECFD2]">Market Value</label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-[#CECFD2]">{similarityCriteria.marketValue.weight}%</span>
                    <span className="text-xs text-[#CECFD2] opacity-75">±€{(similarityCriteria.marketValue.range/1000).toFixed(0)}k</span>
                    <input
                      type="range"
                      min={10000}
                      max={1000000}
                      step={10000}
                      value={similarityCriteria.marketValue.range}
                      onChange={(e) => handleRangeChange('marketValue', e.target.value)}
                      className="w-12 h-1 bg-[#26222E]"
                    />
                  </div>
                </div>
                <Slider
                  id="market-value-weight"
                  min={0}
                  max={100}
                  step={5}
                  value={[similarityCriteria.marketValue.weight]}
                  onValueChange={(value) => handleWeightChange('marketValue', value[0])}
                  className="bg-[#26222E]"
                />
              </div>
            </div>
            
            {/* Column 2: Performance Metrics */}
            <div className="p-4 bg-[#121212]">
              <h3 className="text-sm font-bold mb-3 text-[#CECFD2]">Performance Metrics</h3>
              
              {/* Goals */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="goals-weight" className="text-xs font-medium text-[#CECFD2]">Goals</label>
                  <span className="text-xs font-medium text-[#CECFD2]">{similarityCriteria.goals}%</span>
                </div>
                <Slider
                  id="goals-weight"
                  min={0}
                  max={100}
                  step={5}
                  value={[similarityCriteria.goals]}
                  onValueChange={(value) => handleWeightChange('goals', value[0])}
                  className="bg-[#26222E]"
                />
                <p className="text-xs text-[#CECFD2] opacity-75 mt-1">
                  {selectedPlayer.position?.toLowerCase().includes('striker') || selectedPlayer.position?.toLowerCase().includes('forward')
                    ? 'Default: 20% for forwards'
                    : 'Default: 0% for non-forwards'}
                </p>
              </div>
              
              {/* Assists */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="assists-weight" className="text-xs font-medium text-[#CECFD2]">Assists</label>
                  <span className="text-xs font-medium text-[#CECFD2]">{similarityCriteria.assists}%</span>
                </div>
                <Slider
                  id="assists-weight"
                  min={0}
                  max={100}
                  step={5}
                  value={[similarityCriteria.assists]}
                  onValueChange={(value) => handleWeightChange('assists', value[0])}
                  className="bg-[#26222E]"
                />
              </div>
              
              {/* Minutes Played */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="minutes-played-weight" className="text-xs font-medium text-[#CECFD2]">Minutes Played</label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-[#CECFD2]">{similarityCriteria.minutesPlayed.weight}%</span>
                    <span className="text-xs text-[#CECFD2] opacity-75">±{similarityCriteria.minutesPlayed.range}%</span>
                    <input
                      type="range"
                      min={5}
                      max={50}
                      step={5}
                      value={similarityCriteria.minutesPlayed.range}
                      onChange={(e) => handleRangeChange('minutesPlayed', e.target.value)}
                      className="w-12 h-1 bg-[#26222E]"
                    />
                  </div>
                </div>
                <Slider
                  id="minutes-played-weight"
                  min={0}
                  max={100}
                  step={5}
                  value={[similarityCriteria.minutesPlayed.weight]}
                  onValueChange={(value) => handleWeightChange('minutesPlayed', value[0])}
                  className="bg-[#26222E]"
                />
              </div>
            </div>
            
            {/* Column 3: Other Factors & Summary */}
            <div className="p-4 bg-[#121212]">
              <h3 className="text-sm font-bold mb-3 text-[#CECFD2]">Other Factors</h3>
              
              {/* Nationality */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="nationality-weight" className="text-xs font-medium text-[#CECFD2]">Nationality</label>
                  <span className="text-xs font-medium text-[#CECFD2]">{similarityCriteria.nationality}%</span>
                </div>
                <Slider
                  id="nationality-weight"
                  min={0}
                  max={100}
                  step={5}
                  value={[similarityCriteria.nationality]}
                  onValueChange={(value) => handleWeightChange('nationality', value[0])}
                  className="bg-[#26222E]"
                />
              </div>
              
              {/* Foot */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="foot-weight" className="text-xs font-medium text-[#CECFD2]">Foot</label>
                  <span className="text-xs font-medium text-[#CECFD2]">{similarityCriteria.foot}%</span>
                </div>
                <Slider
                  id="foot-weight"
                  min={0}
                  max={100}
                  step={5}
                  value={[similarityCriteria.foot]}
                  onValueChange={(value) => handleWeightChange('foot', value[0])}
                  className="bg-[#26222E]"
                />
              </div>
              
              {/* Weight Total */}
              <div className="mt-6 p-3 rounded-lg bg-[#26222E] border border-gray-500">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-[#CECFD2]">Total Weight:</span>
                  <span 
                    className={cn(
                      "px-2 py-0.5 rounded-md text-sm font-medium",
                      isCriteriaValid 
                        ? "bg-[#4E46FF] text-[#CECFD2]" 
                        : "bg-[#26222E] text-red-400 border border-red-400"
                    )}
                  >
                    {totalWeight}%
                  </span>
                </div>
                {!isCriteriaValid && (
                  <p className="text-xs text-red-400">
                    Weights must add up to exactly 100%
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button
            onClick={performSimilaritySearch}
            disabled={!isCriteriaValid || isSearchingSimilar}
            className={cn(
              "px-4 py-2 text-sm font-medium shadow-lg rounded-md",
              !isCriteriaValid || isSearchingSimilar 
                ? "bg-[#26222E] text-[#CECFD2] opacity-50" 
                : "bg-[#4E46FF] hover:bg-[#3d37cc] text-[#CECFD2]"
            )}
          >
            {isSearchingSimilar ? (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-[#CECFD2] border-t-transparent"></div>
                <span>Searching...</span>
              </div>
            ) : (
              <>Find Similar Players</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};